import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { jsonFetch } from "@/lib/json-fetch";
import { engagementStandardTierKeys, podShapeKeys, rateCardKeys } from "@/lib/query";
import type { TierConfigView, UpdateTierInput } from "@/schemas/engagement-standard-tiers";

export type { TierConfigView, UpdateTierInput };

/**
 * Every cache holding tier data.
 *
 * The narrower tier list still lives under `podShapeKeys.tiers()` (it predates
 * `engagementStandardTierKeys` and feeds the Accounts tier picker and the Shapes page),
 * and the Rate Card renders the tier → rate card map. Invalidating only one of the three
 * is how a saved tier keeps showing its old values one screen over.
 */
export function invalidateTierCaches(qc: ReturnType<typeof useQueryClient>): void {
  qc.invalidateQueries({ queryKey: engagementStandardTierKeys.all });
  qc.invalidateQueries({ queryKey: podShapeKeys.tiers() });
  qc.invalidateQueries({ queryKey: rateCardKeys.all });
}

/** Settings ▸ Tiers — every tier with its full configuration (R2, R5). */
export function useTiers() {
  return useQuery({
    queryKey: engagementStandardTierKeys.list(),
    queryFn: async (): Promise<TierConfigView[]> => {
      const data = await jsonFetch<{ tiers: TierConfigView[] }>(
        "/api/engagement-standard-tiers/full",
      );
      return data.tiers;
    },
  });
}

/** Edit a tier's configuration defaults. Never its name or its rate card. */
export function useUpdateTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tierId, ...patch }: UpdateTierInput & { tierId: string }) =>
      jsonFetch<TierConfigView>(`/api/engagement-standard-tiers/${tierId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      }),
    onSuccess: (tier) => {
      invalidateTierCaches(qc);
      toast.success(`${tier.name} tier updated`);
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

/*
 * The per-tier Expectation Match penalty step `P` had its own hooks and its own card on
 * the Shapes page (Story E4). Both are gone: `P` is now one field on the tier, edited
 * through useUpdateTier with the rest of its configuration. The dedicated
 * /expectation-penalty-step route stays server-side and stays tested — it is a narrower
 * write than the PATCH and nothing needs to stop honouring it.
 */
